import * as Sentry from "@sentry/react";
import axios from "axios";
import { push, replace } from "redux-first-history";
import { createAction } from "@reduxjs/toolkit";

import type { LocalRecordingStreamPartMetadata } from "../../../lib/localdbNew";
import type { PartsInfoEvent, PartUploadProgressEvent } from "../../../lib/localRecordingNew";
import {
  calcRecordingLayoutUpdate,
  findOneAndOnlyInterviee,
  getVideoParticipantIds,
  isRecordingLayoutDefined,
} from "../../../lib/participants";
import type { RootState } from "../rootReducer";
import type { ActionTypeHelper, AppDispatch } from "../rootStore";
import {
  ArchiveLayout,
  CALL_AUTH_FAILED,
  CALL_AUTH_NEEDED,
  CALL_CHECK_SCREEN_SHARE,
  CALL_CLEAR,
  CALL_DIAL_IN_ENABLED,
  CALL_FAILED,
  CALL_FINISH,
  CALL_FLUSH_LOCAL_RECORDING,
  CALL_LOCAL_RECORDING_UPLOAD_COMPLETION,
  CALL_LOCAL_RECORDING_UPLOAD_FAILED,
  CALL_LOCAL_RECORDING_UPLOAD_INFO,
  CALL_LOCAL_RECORDING_UPLOAD_PENDING,
  CALL_LOCAL_RECORDING_UPLOAD_START,
  CALL_MUTE_ALL,
  CALL_RECEIVED_PEER_INFO,
  CALL_SCREEN_SHARE_FAILED,
  CALL_SEND_CHAT_HISTORY_MESSAGE,
  CALL_SEND_INFO_MESSAGE,
  CALL_SEND_MESSAGE,
  CALL_SEND_PEER_INFO,
  CALL_SET_PASSWORD,
  CALL_SET_PEER_FRAME_RATE,
  CALL_SET_PEER_RESOLUTION,
  CALL_SET_VIDEO_PARTICIPANTS,
  CALL_SETTINGS_SWITCH_DEVICE,
  CALL_SHOW_LOCAL_RECORDING_STATUS,
  CALL_INIT,
  CALL_START_SCREEN_SHARING,
  CALL_START_SETTINGS,
  CALL_STOP_SCREEN_SHARING,
  CALL_STOP_SETTINGS,
  CALL_STOPPED_RECORDING,
  CALL_SWITCH_DEVICE,
  CALL_TOGGLE_CAMERA,
  CALL_TOGGLE_CHAT,
  CALL_TOGGLE_LOCAL_RECORDING,
  CALL_TOGGLE_MICROPHONE,
  CALL_TOGGLE_PEER_AUDIO,
  CALL_UPDATE_ARCHIVE_STATUS,
  USER_ORG_REQUIRES_SESSION_PWD,
  CALL_LOCAL_RECORDING_PART_UPLOAD_FAILED,
  CALL_LOCAL_RECORDING_PART_UPLOAD_SUCCESS,
  CALL_LOCAL_RECORDING_SINGLE_PART_INFO,
  CALL_INITIATE_LOCAL_RECORDING,
  CALL_LOCAL_RECORDING_PART_DONE_RECORDING,
} from "../../actions/types";

import { STATUS_AUTH_FAILED, STATUS_AUTH_NEEDED, STATUS_FINISHED } from "./reducer";

export class CallActionError extends Error {
  constructor(message?: string) {
    super(message);
    this.name = "CallActionError";
  }
}

export class MissingRecordingLayout extends CallActionError {
  constructor(message?: string) {
    super(message);
    this.name = "MissingRecordingLayout";
  }
}

export function stopCallSettings() {
  return { type: CALL_STOP_SETTINGS };
}

export function startCallSettings(skipPublisher = false) {
  return { type: CALL_START_SETTINGS, payload: skipPublisher };
}

export function switchSettingsDevice(audioSource, videoSource) {
  return { type: CALL_SETTINGS_SWITCH_DEVICE, audioSource, videoSource };
}

export function switchCallDevice(audioSource, videoSource, audioOutput) {
  return { type: CALL_SWITCH_DEVICE, audioSource, videoSource, audioOutput };
}

export function setSessionPassword(sessionPassword) {
  return async (dispatch, getState) => {
    try {
      const {
        call: { author, storyId },
      } = getState();
      if (author && storyId) {
        // If current user is the author of the story AND the story session has
        // already been created, then update the password on the backend
        // Story will not exist if the password modal was brought up on call
        // start because a password is required and wasn't provided
        await axios.patch(`/stories/${storyId}/edit`, { sessionPassword });
      }
      dispatch({ type: CALL_SET_PASSWORD, sessionPassword });
    } catch (error) {
      console.log(error);
    }
  };
}

export function startStoryForUpload(projectId: string) {
  return async () => {
    const result = await axios.post("/stories/start", { upload: true });
    const { storyId } = result.data;
    await axios.post(`/stories/${storyId}/associate-project`, { projectId });

    return storyId;
  };
}

/**
 * Gets invoked when video session component is mounted in order to initiate a
 * conference call
 *
 * @param path      path component from browser's URL
 * @param urlParams params component from browser's URL
 * @param groupId   optional ID of the group from which the call was started
 * @returns         a boolean indicating whether or not we began the call. The
 *                  reason this return value is important is because we have a
 *                  bit of a state machine going on (in part because we are lazy
 *                  and overloaded frontend component for more than one thing)
 *                  Some URLs will simply make an API call and then upon
 *                  completion will redirect the browser to a different URL, which
 *                  will cause UI to unload/reload video session component. In the
 *                  past during unload video session would always attempt to clear
 *                  out the call but that created a race because we were clearing a
 *                  call that was actually being created. Now if we return false,
 *                  that's an indication that we did one of those redirects and
 *                  NOTHING should be cleared
 */
export function startCall(path: string, urlParams, groupId: string): ActionTypeHelper<Promise<boolean>> {
  const storyIdFromURL = urlParams.story_id;
  const projectIdFromUrl = urlParams.project_id;
  const prepsessionKeyFromUrl = urlParams.key;
  return async (dispatch, getState) => {
    const {
      call: { sessionPassword },
      featureFlags: { flags: userFlags },
    } = getState() as RootState;
    const endOfPath = path.substring(path.lastIndexOf("/"));
    let callWasStarted = false;

    try {
      const params = sessionPassword ? { password: sessionPassword } : {};
      let newPath = null;
      let result;

      if (endOfPath === "/start" && storyIdFromURL) {
        result = await axios.get(`/stories/${storyIdFromURL}/start`);
        const { archiveStatus } = result.data;
        dispatch({ type: CALL_UPDATE_ARCHIVE_STATUS, status: archiveStatus });
        newPath = `/stories/${storyIdFromURL}`;
      } else if (storyIdFromURL) {
        result = await axios.post(`/stories/${storyIdFromURL}/join`, {
          ...params,
          visitorId: getState().auth.visitorId,
        });
        // There's a pretty subtle state machine using the URL redirects.
        dispatch(initializeCall(result.data));
        callWasStarted = true;
        dispatch({ type: CALL_UPDATE_ARCHIVE_STATUS, status: result.data.archiveStatus });
      } else if (prepsessionKeyFromUrl) {
        //  start new story from a prepared session
        const url = `prepsession/${urlParams.group_name}/${urlParams.custom_url_title}/${prepsessionKeyFromUrl}`;
        const res = await axios.post(url, params);
        if (!res.data) {
          newPath = "/not-found";
        } else {
          const { archiveStatus } = res.data;
          dispatch({ type: CALL_UPDATE_ARCHIVE_STATUS, status: archiveStatus });
          newPath = `/stories/${res.data.storyId}`;

          // if (userFlags?.enableTwilioVideo) {
          //   newPath = `/session/${res.data.storyId}`;
          // } else {
          //   newPath = `/stories/${res.data.storyId}`;
          // }
        }
      } else {
        const startParams = {
          password: sessionPassword,
          group_id: groupId,
          localRecordingStreaming: userFlags?.streamingUpload,
          localRecordingMimeType: userFlags?.localRecordingMimeType,
          localRecordingVideoBitrate: userFlags?.localVideoRecordingBitrate,
          localRecordingMaxResolution: userFlags?.localVideoMaxResolution,
          disableAutomaticResolutionSwitching: userFlags?.disableAutomaticResolutionSwitching,
        };
        result = await axios.post("/stories/start", startParams);
        newPath = `/projects/${projectIdFromUrl}/stories/${result.data.storyId}/call`;
      }
      if (newPath) dispatch(replace(newPath));
    } catch (error) {
      const { response } = error;
      const { status, data } = response || {};
      if (status === 400 && data?.error?.code === 100) {
        // We will get this status/code combo when a new session is launched
        // but org config requires the user to specify a password for the new
        // session
        dispatch({ type: CALL_AUTH_NEEDED, payload: { author: true } });
        dispatch({ type: USER_ORG_REQUIRES_SESSION_PWD });
      } else if (status === 401) {
        dispatch({
          type: sessionPassword ? CALL_AUTH_FAILED : CALL_AUTH_NEEDED,
          payload: {},
        });
      } else {
        Sentry.captureMessage("Call Start Failed");
        Sentry.captureException(error);
        dispatch({ type: CALL_FAILED, error });
      }
    }
    return callWasStarted;
  };
}

// TODO: This interface defines the payload returned directly from /stories/<id>/join API call
// Ideally, we should have this defined in common/api_types and we will... shortly, but for now
// throwing this definition here just enough so we can write some type-safe code around the
// members we are consuming (i.e. for now this is not necessarily the full definition of what the
// API is returning us)
// -- DXM 2022-04-12
export interface InitializeCall {
  token: string;
  guestToken?: string;
  dolbyConference?: boolean;
}

export const initializeCall = createAction<InitializeCall>(CALL_INIT);

export function finishCall(path, projectId): ActionTypeHelper<Promise<void>> {
  return async (dispatch, getState) => {
    const { call, localRecording, auth } = getState();
    if (
      !call.status ||
      call.status === STATUS_FINISHED ||
      call.status === STATUS_AUTH_FAILED ||
      call.status === STATUS_AUTH_NEEDED
    ) {
      // There is some extreme state machine logic going on. When a session is launched from
      // a prepared session, we have...
      //   1. Instance of VideoSession gets created
      //   2. that instance will call startCall(), return of that call is a storyId
      //   3. route gets rewritten to /stories/<id>
      //   4. Second instance of VideoSession is created (willMount is called prior to willUnmount)
      //   5. another call to startCall() takes place
      //   6. First instance of VideoSession gets unmounted which results in finishCall()
      //
      // Thankfully in this crazy sequence of events, not enough cycles take place and so call.status
      // is not set to anything "in progress" and we simply return without cancelling anything even
      // though there are already things inflight
      //
      // There's got to be a better way as calling 'finish' after 'start' we would expect stuff to
      // finalize. Or maybe not call 'finish' when we really don't mean to do that?
      return;
    }

    let allComplete = false;

    if (call.featureFlags?.localRecordingStreaming) {
      const storyId = call.storyId;
      const recordings =
        localRecording.callStates[storyId]?.participants?.[auth?.user_id]?.localRecording?.recordings || {};
      allComplete = Object.values(recordings).every((rec) => rec.uploadState === "UPLOAD_SUCCESSFUL");
    } else {
      const completions = localRecording.completions;
      const uploads = localRecording.uploads;
      allComplete = uploads.every((u) => completions.find((c) => c.streamId === u.streamId) != null);
    }

    let newPath;
    if (call.creator && call.recorded) {
      if (call.recording) {
        console.log("stop recording get call");
        await dispatch(stopRecording());
      }

      const endOfPath = path.substring(path.lastIndexOf("/"));
      newPath =
        endOfPath === "/call"
          ? `/projects/${projectId}/stories/${call.storyId}/conclude`
          : `/stories/${call.storyId}/conclude`;
    } else if (!allComplete) {
      newPath = "/status/local-recording";
    } else {
      newPath = "/";
    }
    dispatch({ type: CALL_STOP_SCREEN_SHARING });
    dispatch({ type: CALL_FINISH });

    if (newPath === "/" && call.prepsession?.redirection_url) {
      window.location.href = call.prepsession.redirection_url;
    } else {
      console.log("call from redirect /");
      // dispatch(push(`/projects/${projectId}`))
      dispatch(push(newPath));
    }
  };
}

export function clearCall() {
  return { type: CALL_CLEAR };
}

export function toggleMicrophone() {
  return { type: CALL_TOGGLE_MICROPHONE };
}

export function toggleCamera() {
  return { type: CALL_TOGGLE_CAMERA };
}

export function toggleChat() {
  return { type: CALL_TOGGLE_CHAT };
}

export function updateParticipantsLocal(payload) {
  return { type: CALL_RECEIVED_PEER_INFO, payload };
}

export function updateParticipantsRemote(payload) {
  // It is expected that this action will ONLY get performed by the participant who
  // is doing the actual editing/controlling of participant record(s)
  return { type: CALL_SEND_PEER_INFO, payload };
}

function stopRecording() {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const { call } = getState();
    const result = axios.post(`/stories/${call.storyId}/stop_recording`);
    // CALL_STOPPED_RECORDING is dispatched both when we actively stop recording,
    // and when we recieve a signal that someone else stopped the call recording
    dispatch({ type: CALL_STOPPED_RECORDING, payload: { source: "user" } });
    return result;
  };
}

export function toggleRecording() {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    let { call } = getState();
    let result;
    if (call.recording) {
      result = await dispatch(stopRecording());
    } else {
      const recordingLayoutDefined = isRecordingLayoutDefined(call.participants);

      if (!recordingLayoutDefined) {
        dispatch(setDefaultRecordingLayout());
        call = getState().call;
      }

      const videoParticipantIds = getVideoParticipantIds(call.participants, true);
      if (
        !recordingLayoutDefined &&
        videoParticipantIds != null &&
        !videoParticipantIds.length &&
        call.prepsession?.archiveLayout === ArchiveLayout.JUST_INTERVIEWEE
      ) {
        throw new MissingRecordingLayout();
      }

      // In the handler for this function, execute a server-originates command to indicate that the call recording has stopped
      // do the same in the stop recording endpoint...
      result = await axios.post(`/stories/${call.storyId}/start_recording`, {
        localRecording: call.localRecording,
        videoParticipants: videoParticipantIds,
      });

      if (call.localRecording) {
        dispatch({ type: CALL_INITIATE_LOCAL_RECORDING });
      }

      dispatch({ type: CALL_SET_VIDEO_PARTICIPANTS, payload: videoParticipantIds });
    }
    dispatch({
      type: CALL_UPDATE_ARCHIVE_STATUS,
      status: result.data.archiveStatus,
    });
  };
}

export function enableDialIn() {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const { call } = getState();
    const result = await axios.post(`/stories/${call.storyId}/enable_dial_in`);
    dispatch({
      type: CALL_DIAL_IN_ENABLED,
      data: result.data,
    });
  };
}

export function toggleLocalRecording() {
  return { type: CALL_TOGGLE_LOCAL_RECORDING };
}

export function pendingLocalRecordingUpload(stream) {
  return { type: CALL_LOCAL_RECORDING_UPLOAD_PENDING, data: { stream } };
}

export function startLocalRecordingUpload(stream) {
  return { type: CALL_LOCAL_RECORDING_UPLOAD_START, data: { stream } };
}

export function completeLocalRecordingUpload(stream) {
  return { type: CALL_LOCAL_RECORDING_UPLOAD_COMPLETION, data: { stream } };
}

export function localRecordingPartDoneRecording(stream, partId) {
  return { type: CALL_LOCAL_RECORDING_PART_DONE_RECORDING, data: { stream, partId } };
}

export function failedLocalRecordingUpload(stream, message = "") {
  return { type: CALL_LOCAL_RECORDING_UPLOAD_FAILED, data: { stream, message } };
}

export function failedLocalRecordingPartUpload(stream, partId, message) {
  return { type: CALL_LOCAL_RECORDING_PART_UPLOAD_FAILED, data: { stream, partId, message } };
}

export function completeLocalRecordingPartUpload(stream, partId) {
  return { type: CALL_LOCAL_RECORDING_PART_UPLOAD_SUCCESS, data: { stream, partId } };
}

export function localRecordingSinglePartInfo(stream, part: LocalRecordingStreamPartMetadata, isRecording) {
  return { type: CALL_LOCAL_RECORDING_SINGLE_PART_INFO, data: { stream, part, isRecording } };
}

export function localRecordingUploadProgress(stream, evt: PartUploadProgressEvent) {
  return {
    type: CALL_LOCAL_RECORDING_UPLOAD_PENDING,
    data: {
      stream,
      ...evt,
    },
  };
}

export function localRecordingUploadInfo(stream, evt: PartsInfoEvent) {
  return {
    type: CALL_LOCAL_RECORDING_UPLOAD_INFO,
    data: {
      stream,
      ...evt,
    },
  };
}

export function togglePeerAudio(peerID) {
  return { type: CALL_TOGGLE_PEER_AUDIO, peerID };
}

export function setPeerResolution(peerID, newResolution) {
  return { type: CALL_SET_PEER_RESOLUTION, peerID, newResolution };
}

export function setPeerFrameRate(peerID, newFrameRate) {
  return { type: CALL_SET_PEER_FRAME_RATE, peerID, newFrameRate };
}

export function muteAll() {
  return { type: CALL_MUTE_ALL };
}

export function checkScreenShare() {
  return { type: CALL_CHECK_SCREEN_SHARE };
}

interface ParticipantUpdateEntry {
  visitorId: string;
  localRecordingId?: string;
  props: { [key: string]: unknown };
}

export function setDefaultRecordingLayout(): ActionTypeHelper {
  return (dispatch, getState) => {
    const { prepsession, participants } = getState().call;
    const layout = prepsession?.archiveLayout ?? ArchiveLayout.ALL;
    let updates: ReturnType<typeof calcRecordingLayoutUpdate>;
    if (layout === ArchiveLayout.JUST_INTERVIEWEE) {
      const interviewee = findOneAndOnlyInterviee(participants);
      updates = calcRecordingLayoutUpdate(participants, false, interviewee ? { [interviewee[0]]: true } : null);
    } else {
      // Use same calculation we use when recording is started. If ALL participants are recorded,
      // we will pass in 'null' for list of IDs and then any new participant will also get recorded.
      // If the list here is not 'null' new participants will be off by default so we replicate same
      // behavior here.
      const videoParticipantIds = getVideoParticipantIds(participants, true, true);
      updates = calcRecordingLayoutUpdate(participants, videoParticipantIds == null);
    }
    dispatch(broadcastParticipantUpdate(updates));
  };
}

export function broadcastParticipantUpdate(entries: ParticipantUpdateEntry[]): ActionTypeHelper;
export function broadcastParticipantUpdate(visitorId: string, props: { [key: string]: unknown }): ActionTypeHelper;
export function broadcastParticipantUpdate(
  visitorId: string,
  localRecordingId: string,
  props: { [key: string]: unknown }
): ActionTypeHelper;
export function broadcastParticipantUpdate(
  visitorIdOrEntries: string | ParticipantUpdateEntry[],
  localRecordingIdOrProps?: string | { [key: string]: unknown },
  props?: { [key: string]: unknown }
): ActionTypeHelper {
  return (dispatch: AppDispatch) => {
    const buildUpdate = (visitorId: string, localRecordingId: string | null, _props: { [key: string]: unknown }) => {
      const update: { [key: string]: unknown } = {};

      Object.entries(_props).forEach(([key, value]) => {
        update[key] = { $set: value };
      });
      return {
        visitorId,
        data: !localRecordingId
          ? update
          : {
              localRecordings: {
                [localRecordingId]: update,
              },
            },
      };
    };

    const localRecordingId = typeof localRecordingIdOrProps === "string" ? localRecordingIdOrProps : null;
    const _props = typeof localRecordingIdOrProps === "string" ? props : localRecordingIdOrProps;
    const payload = Array.isArray(visitorIdOrEntries)
      ? visitorIdOrEntries.map((x) => buildUpdate(x.visitorId, x.localRecordingId, x.props))
      : buildUpdate(visitorIdOrEntries, localRecordingId, _props);

    dispatch(updateParticipantsLocal(payload));
    dispatch(updateParticipantsRemote(payload));
  };
}

export function toggleScreenShare() {
  return (dispatch, getState) => {
    const { call } = getState();
    if (!call.screenShareSupported) {
      console.log("CALL_SCREEN_SHARE_FAILED");
      dispatch({ type: CALL_SCREEN_SHARE_FAILED });
    } else if (call.screenSharing) {
      dispatch({ type: CALL_STOP_SCREEN_SHARING });
    } else {
      dispatch({ type: CALL_START_SCREEN_SHARING });
    }
  };
}

export const sendMessage = (metaValues): ActionTypeHelper => {
  const { newMessage, messages, sender, typingSender, typing, sendHistoryChat } = metaValues;
  console.log("sendMessage metavalues:", metaValues);
  return (dispatch, getState) => {
    const { call } = getState();
    if (!call.status || call.status === STATUS_FINISHED) {
      dispatch({ type: CALL_FINISH });
    }
    if (sendHistoryChat && sendHistoryChat.size > 0) {
      dispatch({
        type: CALL_SEND_CHAT_HISTORY_MESSAGE,
        historyChat: sendHistoryChat,
      });
    } else if (typingSender) {
      dispatch({
        type: CALL_SEND_INFO_MESSAGE,
        sender: typingSender,
        typing: typing,
      });
    } else {
      dispatch({
        type: CALL_SEND_MESSAGE,
        newMessage: newMessage,
        messages: messages,
        sender: sender,
      });
    }
  };
};

export function toggleLocalRecordingStatus(show?: boolean) {
  return { type: CALL_SHOW_LOCAL_RECORDING_STATUS, payload: show };
}

export const flushLocalRecording = (): ActionTypeHelper<Promise<void>> => (dispatch, getState, extra) => {
  const isLocallyRecording = () => {
    const {
      call: { recording, localRecording },
    } = getState();
    return recording && localRecording;
  };

  return new Promise<void>((resolve): void => {
    if (!isLocallyRecording()) {
      resolve();
    } else {
      dispatch({ type: CALL_FLUSH_LOCAL_RECORDING });
      const unsubscribe = extra.store.subscribe(() => {
        if (!isLocallyRecording()) {
          unsubscribe();
          resolve();
        }
      });
    }
  });
};
